import type {
  ChangeOrder,
  EnhancedDeal,
  ProjectTask,
  ProposalBuilderData,
  ProposalNodeSet,
  RfqPackage,
  RfqProject,
  RfqSubmission,
  TaskGroup,
  TimberProject,
} from '../types';
import type { CashFlowProjection } from './cashFlowProjection';
import { isOpenReceivable, isProjectedReceivable } from './cashFlowProjection';
import { toMonthKey } from './financesForecast';
import {
  isGroupTimelineConflictTitle,
  supplierFocusTaskStartDate,
} from './groupTimelineConflicts';
import { listGroupTimelineFamilyParentTasks } from './groupTimeline';
import { pickTimberProjectForDealSchedule } from './paymentDueFlags';
import { isStructureClientSelected } from './proposalPublicGrandTotals';
import type { ReadyToBillRow } from './readyToBill';
import { rollupDatesFromChildren } from './subtaskDateRollup';
import { getSubtaskStructures } from './subtaskStructures';
import type { SupplierBill } from './supplierBills';
import {
  approvedSubmissionIdsOnRfqProject,
  assignableStructuresAcrossPackages,
  awardedReleaseKeysByOrg,
  quotePackagesForOrg,
} from './rfqApprovedQuoteCoverage';
import { isReferenceRelease } from './rfqReleasePurpose';
import {
  isTimberQuoteFullScope,
  submissionCoversStructureIds,
  timberQuoteAssignableStructures,
} from './timberQuoteStreamKey';
import { buildWorkTypeSummary } from './vendorAdminProjectSummary';
import { normalizeWorkType } from './vendorHelpers';

export type ForecastQuoteCoverageKind = 'timber' | 'cnc';

export type ForecastQuoteCoverageMarker = {
  monthKey: string;
  dealId: string;
  projectId: string;
  projectLabel: string;
  kind: ForecastQuoteCoverageKind;
  taskId: string;
  taskTitle: string;
  taskStart: Date;
  /** Midpoint of the matched task / structure subtask — cash lands here, not on Today. */
  cashDate: Date;
  /** Proposal-node cost used on the cash line until a quote is awarded. */
  amount: number;
  structureId?: string;
  structureName?: string;
  /** Set when some (not all) selected structures already have an approved quote. */
  uncoveredStructureNames?: string[];
};

type NeededQuoteStructure = {
  id: string;
  name: string;
  amount: number;
};

export type QuotePlaceholderApSlice = {
  monthKey: string;
  amount: number;
  dealId: string;
  projectId: string;
  projectLabel: string;
  kind: ForecastQuoteCoverageKind;
  taskId?: string;
  dueDate: Date | null;
  structureName?: string;
};

export type BuildForecastQuoteCoverageMarkersInput = {
  moneyInRows: ReadyToBillRow[];
  projection?: CashFlowProjection | null;
  horizonMonthKeys?: string[];
  supplierBills: SupplierBill[];
  timberProjects: TimberProject[];
  enhancedDeals: EnhancedDeal[];
  rfqProjects: RfqProject[];
  rfqPackages: RfqPackage[];
  rfqSubmissions: RfqSubmission[];
  projectTasks: ProjectTask[];
  taskGroups: TaskGroup[];
  projectIdByDealId: Map<string, string>;
  projectNameById: Map<string, string>;
  proposalsByDeal?: Record<string, ProposalBuilderData | null | undefined>;
  changeOrders?: ChangeOrder[];
  now?: Date;
};

const EMPTY_ORG_LOOKUP = new Map<string, { name?: string }>();

function moneyish(value: unknown): number {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
}

function qtyOf(quantity: number | undefined): number {
  return quantity && quantity > 0 ? quantity : 1;
}

function nodeSetTrackAmount(kind: ForecastQuoteCoverageKind, ns: ProposalNodeSet): number {
  const qty = qtyOf(ns.quantity);
  return kind === 'timber'
    ? moneyish(ns.materials?.timberTotal) * qty
    : moneyish(ns.production?.total) * qty;
}

function addNeededStructure(
  byId: Map<string, NeededQuoteStructure>,
  id: string,
  name: string,
  amount: number,
): void {
  const key = id.trim();
  if (!key || amount <= 0) return;
  const existing = byId.get(key);
  if (existing) {
    existing.amount += amount;
    return;
  }
  byId.set(key, { id: key, name: (name || '').trim() || key, amount });
}

/** Selected proposal / CO structures that still need a timber or CNC quote. */
export function neededQuoteStructuresForTrack(
  kind: ForecastQuoteCoverageKind,
  proposal: ProposalBuilderData | null | undefined,
  changeOrders: ChangeOrder[] | undefined,
  dealId: string,
): NeededQuoteStructure[] {
  const byId = new Map<string, NeededQuoteStructure>();
  if (proposal) {
    const phased = !!proposal.phasingEnabled && (proposal.phases?.length || 0) > 0;
    for (const ns of proposal.nodeSets || []) {
      if (!isStructureClientSelected(ns, proposal.structureSelections, phased)) continue;
      addNeededStructure(byId, ns.id, ns.name || ns.id, nodeSetTrackAmount(kind, ns));
    }
  }
  for (const co of changeOrders ?? []) {
    if (co.dealId !== dealId || co.status !== 'approved') continue;
    if (co.budgetCostByBucket) continue;
    if (co.pricingMode === 'builder' && co.builderNodeSets) {
      for (const ns of co.builderNodeSets) {
        if (ns.enabled === false) continue;
        addNeededStructure(byId, ns.id, ns.name || ns.id, nodeSetTrackAmount(kind, ns));
      }
    }
  }
  return Array.from(byId.values()).map((row) => ({
    ...row,
    amount: Math.round(row.amount),
  }));
}

function approvedCoBucketAmount(
  kind: ForecastQuoteCoverageKind,
  changeOrders: ChangeOrder[] | undefined,
  dealId: string,
): number {
  let sum = 0;
  for (const co of changeOrders ?? []) {
    if (co.dealId !== dealId || co.status !== 'approved' || !co.budgetCostByBucket) continue;
    sum += kind === 'timber'
      ? moneyish(co.budgetCostByBucket.timber)
      : moneyish(co.budgetCostByBucket.production);
  }
  return sum;
}

/** Timber list / CNC production totals from selected proposal node-sets (+ approved COs). */
export function proposalNodeQuoteAmount(
  kind: ForecastQuoteCoverageKind,
  proposal: ProposalBuilderData | null | undefined,
  changeOrders: ChangeOrder[] | undefined,
  dealId: string,
): number {
  const structures = neededQuoteStructuresForTrack(kind, proposal, changeOrders, dealId);
  const fromNodes = structures.reduce((sum, row) => sum + row.amount, 0);
  return Math.round(fromNodes + approvedCoBucketAmount(kind, changeOrders, dealId));
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/** Inclusive calendar midpoint of a task / subtask window. */
export function taskWindowMidpoint(start: Date, end: Date): Date {
  const s = startOfLocalDay(start);
  const e = startOfLocalDay(end);
  if (e.getTime() <= s.getTime()) return s;
  const days = Math.round((e.getTime() - s.getTime()) / 86_400_000);
  return new Date(s.getFullYear(), s.getMonth(), s.getDate() + Math.floor(days / 2));
}

function childrenOf(parentId: string, tasks: ProjectTask[]): ProjectTask[] {
  return tasks.filter((t) => t.parentTaskId === parentId);
}

function focusTaskWindow(
  parent: ProjectTask,
  allTasks: ProjectTask[],
): { startDate: Date; endDate: Date } | null {
  const kids = parent.id ? childrenOf(parent.id, allTasks) : [];
  if (kids.length > 0) return rollupDatesFromChildren(kids);
  return rollupDatesFromChildren([parent]);
}

function normStructureName(name: string | undefined): string {
  return (name || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function findStructureSubtask(
  parent: ProjectTask,
  allTasks: ProjectTask[],
  structure: NeededQuoteStructure,
): ProjectTask | null {
  const kids = parent.id ? childrenOf(parent.id, allTasks) : [];
  const id = structure.id.trim();
  const name = normStructureName(structure.name);
  for (const kid of kids) {
    for (const ref of getSubtaskStructures(kid)) {
      if (id && ref.id === id) return kid;
      if (name && normStructureName(ref.name) === name) return kid;
    }
  }
  return null;
}

function monthKeyFromDate(d: Date): string {
  return toMonthKey(startOfMonth(d));
}

function dealById(deals: EnhancedDeal[]): Map<string, EnhancedDeal> {
  const m = new Map<string, EnhancedDeal>();
  for (const d of deals) if (d.id) m.set(d.id, d);
  return m;
}

function isQualifyingHubProject(
  dealId: string,
  deal: EnhancedDeal | undefined,
  projects: TimberProject[],
): boolean {
  const hub = projects.filter((p) => p.dealId === dealId && p.id);
  if (!hub.length) return false;
  if (hub.some((p) => p.status === 'completed')) return false;
  if (hub.some((p) => p.phase === 'contracted' || p.phase === undefined)) return true;
  return deal?.outcome === 'won';
}

function horizonKeysFromInput(input: BuildForecastQuoteCoverageMarkersInput): Set<string> {
  if (input.horizonMonthKeys?.length) return new Set(input.horizonMonthKeys);
  return projectionMonthKeys(input.projection ?? null);
}

function projectionMonthKeys(projection: CashFlowProjection | null): Set<string> {
  const keys = new Set<string>();
  for (const m of projection?.months ?? []) keys.add(m.monthKey);
  return keys;
}

function dealHasForecastRevenueExposure(
  dealId: string,
  moneyInRows: ReadyToBillRow[],
  horizonKeys: Set<string>,
  now: Date,
): boolean {
  for (const row of moneyInRows) {
    if (row.dealId !== dealId) continue;
    if (!isOpenReceivable(row)) continue;
    if (isProjectedReceivable(row, now)) return true;
    if (!row.dueDate) {
      if (horizonKeys.size > 0) return true;
      continue;
    }
    const key = monthKeyFromDate(row.dueDate);
    if (horizonKeys.has(key)) return true;
  }
  return false;
}

function rfqProjectsForDealTrack(
  dealId: string,
  kind: ForecastQuoteCoverageKind,
  rfqProjects: RfqProject[],
): RfqProject[] {
  const workType = kind === 'timber' ? 'timber_rfq' : 'cnc_quote';
  return rfqProjects.filter(
    (p) => p.dealId === dealId && normalizeWorkType(p.workType) === workType,
  );
}

function rfqProjectsHaveAward(projects: RfqProject[]): boolean {
  for (const p of projects) {
    if (p.approvedSupplierOrgId) return true;
    if ((p.approvedSubmissionIds ?? []).length > 0) return true;
    for (const id of Object.values(p.approvedSupplierOrgIdByRelease ?? {})) {
      if (id) return true;
    }
    for (const ids of Object.values(p.approvedSupplierOrgIdsByRelease ?? {})) {
      if (ids.length > 0) return true;
    }
    for (const ids of Object.values(p.approvedSubmissionIdsByRelease ?? {})) {
      if (ids.length > 0) return true;
    }
  }
  return false;
}

/** Non-void timber/CNC bill — the official forecast amount replaces proposal nodes. */
export function dealHasOfficialQuoteBill(
  dealId: string,
  kind: ForecastQuoteCoverageKind,
  supplierBills: SupplierBill[],
): boolean {
  const category = kind === 'timber' ? 'timber' : 'cnc';
  return supplierBills.some(
    (b) => b.dealId === dealId && b.category === category && b.status !== 'void',
  );
}

/**
 * Structures a supplier was asked to quote, unioned across every quoting package
 * on the award's release(s). A newer, narrower package on a later release (or a
 * reference release shared for production) must not shrink the awarded scope.
 */
function awardedAssignableStructures(
  project: RfqProject,
  orgId: string,
  rfqPackages: RfqPackage[],
  releaseKeys: Set<string> | undefined,
  directPackage?: RfqPackage,
) {
  if (directPackage && !isReferenceRelease(directPackage)) {
    return timberQuoteAssignableStructures(project, orgId, directPackage);
  }
  const packages = quotePackagesForOrg(project.id, orgId, rfqPackages, releaseKeys);
  return assignableStructuresAcrossPackages(project, orgId, packages);
}

function addCoveredIds(target: Set<string>, ids: string[]): void {
  for (const id of ids) {
    if (id) target.add(id);
  }
}

/**
 * Map quote/package structure claims onto the live proposal node ids.
 * RFQ snapshots often keep older node ids; name match still counts as coverage.
 * Full-scope quotes cover every needed structure.
 */
function neededIdsCoveredByClaim(input: {
  needed: NeededQuoteStructure[];
  claimedIds: string[];
  claimedNames?: string[];
  fullScope?: boolean;
}): string[] {
  if (input.fullScope) return input.needed.map((row) => row.id);
  const byId = new Map(input.needed.map((row) => [row.id, row.id]));
  const byName = new Map(input.needed.map((row) => [normStructureName(row.name), row.id]));
  const out: string[] = [];
  const seen = new Set<string>();
  const claims = [...input.claimedIds, ...(input.claimedNames ?? [])];
  for (const raw of claims) {
    const token = (raw || '').trim();
    if (!token) continue;
    const hit = byId.get(token) || byName.get(normStructureName(token));
    if (!hit || seen.has(hit)) continue;
    seen.add(hit);
    out.push(hit);
  }
  return out;
}

function coveredQuoteStructureIds(input: {
  dealId: string;
  kind: ForecastQuoteCoverageKind;
  needed: NeededQuoteStructure[];
  rfqProjects: RfqProject[];
  rfqPackages: RfqPackage[];
  rfqSubmissions: RfqSubmission[];
}): Set<string> {
  const covered = new Set<string>();
  const neededIds = input.needed.map((row) => row.id);
  const projects = rfqProjectsForDealTrack(input.dealId, input.kind, input.rfqProjects);

  for (const project of projects) {
    const approvedIds = approvedSubmissionIdsOnRfqProject(project);
    const awardedByOrg = awardedReleaseKeysByOrg(project);
    const approvedSubs = input.rfqSubmissions.filter((sub) => {
      if (!sub.id || !approvedIds.has(sub.id)) return false;
      if (sub.rfqProjectId && project.id && sub.rfqProjectId !== project.id) return false;
      return true;
    });
    const orgsWithApprovedSubs = new Set(
      approvedSubs.map((sub) => sub.supplierOrgId).filter(Boolean),
    );

    for (const sub of approvedSubs) {
      const directPkg = sub.rfqPackageId
        ? input.rfqPackages.find((row) => row.id === sub.rfqPackageId)
        : undefined;
      const assignable = awardedAssignableStructures(
        project,
        sub.supplierOrgId,
        input.rfqPackages,
        awardedByOrg.get(sub.supplierOrgId),
        directPkg,
      );
      const assignedIds = assignable.map((row) => row.id);
      const assignedNames = assignable.map((row) => row.name);
      if (isTimberQuoteFullScope(sub)) {
        if (assignedIds.length === 0) {
          addCoveredIds(covered, neededIds);
          continue;
        }
        const matched = neededIdsCoveredByClaim({
          needed: input.needed,
          claimedIds: assignedIds,
          claimedNames: assignedNames,
        });
        // Stale snapshot ids that map onto none of the live nodes: treat as a full-job award.
        addCoveredIds(covered, matched.length > 0 ? matched : neededIds);
        continue;
      }
      const claimedIds = submissionCoversStructureIds(sub, assignedIds);
      const quotedNames = (sub.quotedStructures ?? []).map((row) => row.name);
      const matched = neededIdsCoveredByClaim({
        needed: input.needed,
        claimedIds,
        claimedNames: quotedNames,
      });
      if (matched.length > 0) {
        addCoveredIds(covered, matched);
        continue;
      }
      const assignedMatched = neededIdsCoveredByClaim({
        needed: input.needed,
        claimedIds: assignedIds,
        claimedNames: assignedNames,
      });
      // Stale snapshot ids that map onto none of the live nodes: treat as a full-job award.
      if (assignedMatched.length === 0) addCoveredIds(covered, neededIds);
    }

    for (const [orgId, releaseKeys] of Array.from(awardedByOrg.entries())) {
      if (orgsWithApprovedSubs.has(orgId)) continue;
      const assignable = awardedAssignableStructures(project, orgId, input.rfqPackages, releaseKeys);
      const assignedIds = assignable.map((row) => row.id);
      const assignedNames = assignable.map((row) => row.name);
      if (assignedIds.length === 0) {
        addCoveredIds(covered, neededIds);
        continue;
      }
      const matched = neededIdsCoveredByClaim({
        needed: input.needed,
        claimedIds: assignedIds,
        claimedNames: assignedNames,
      });
      // Snapshot ids that don't map onto the live proposal are treated as full-job award.
      addCoveredIds(covered, matched.length > 0 ? matched : neededIds);
    }
  }

  return covered;
}

function isTrackApprovedLegacy(
  dealId: string,
  kind: ForecastQuoteCoverageKind,
  rfqProjects: RfqProject[],
  rfqPackages: RfqPackage[],
  rfqSubmissions: RfqSubmission[],
  supplierBills: SupplierBill[],
): boolean {
  const workType = kind === 'timber' ? 'timber_rfq' : 'cnc_quote';
  const summary = buildWorkTypeSummary(
    dealId,
    workType,
    rfqProjects,
    rfqPackages,
    rfqSubmissions,
    EMPTY_ORG_LOOKUP,
  );
  if (summary.approvedOrgId) return true;
  if (rfqProjectsHaveAward(rfqProjectsForDealTrack(dealId, kind, rfqProjects))) return true;
  return dealHasOfficialQuoteBill(dealId, kind, supplierBills);
}

type TrackCoverage = {
  fullyCovered: boolean;
  uncovered: NeededQuoteStructure[];
  partial: boolean;
};

function trackQuoteCoverage(input: {
  dealId: string;
  kind: ForecastQuoteCoverageKind;
  proposal: ProposalBuilderData | null | undefined;
  changeOrders: ChangeOrder[] | undefined;
  rfqProjects: RfqProject[];
  rfqPackages: RfqPackage[];
  rfqSubmissions: RfqSubmission[];
  supplierBills: SupplierBill[];
}): TrackCoverage {
  const needed = neededQuoteStructuresForTrack(
    input.kind,
    input.proposal,
    input.changeOrders,
    input.dealId,
  );
  if (needed.length === 0) {
    return {
      fullyCovered: isTrackApprovedLegacy(
        input.dealId,
        input.kind,
        input.rfqProjects,
        input.rfqPackages,
        input.rfqSubmissions,
        input.supplierBills,
      ),
      uncovered: [],
      partial: false,
    };
  }

  const covered = coveredQuoteStructureIds({
    dealId: input.dealId,
    kind: input.kind,
    needed,
    rfqProjects: input.rfqProjects,
    rfqPackages: input.rfqPackages,
    rfqSubmissions: input.rfqSubmissions,
  });
  const uncovered = needed.filter((row) => !covered.has(row.id));
  const matchedNeeded = needed.length - uncovered.length;
  if (matchedNeeded === 0 && dealHasOfficialQuoteBill(input.dealId, input.kind, input.supplierBills)) {
    return { fullyCovered: true, uncovered: [], partial: false };
  }
  return {
    fullyCovered: uncovered.length === 0,
    uncovered,
    partial: covered.size > 0 && uncovered.length > 0,
  };
}

function openFocusParent(
  projectId: string,
  family: 'procurement' | 'production',
  timberProjects: TimberProject[],
  taskGroups: TaskGroup[],
  projectTasks: ProjectTask[],
): ProjectTask | null {
  const project = timberProjects.find((p) => p.id === projectId);
  if (!project) return null;
  const parents = listGroupTimelineFamilyParentTasks({
    family,
    projects: [project],
    groups: taskGroups.filter((g) => g.projectId === projectId),
    tasks: projectTasks.filter((t) => t.projectId === projectId),
  });
  for (const parent of parents) {
    if (parent.status === 'completed') continue;
    if (!isGroupTimelineConflictTitle(parent.title, family)) continue;
    if (!parent.id) continue;
    if (!supplierFocusTaskStartDate(parent, projectTasks)) continue;
    return parent;
  }
  return null;
}

function trackShouldWarn(
  kind: ForecastQuoteCoverageKind,
  dealId: string,
  hasWorkOrder: boolean,
  projectId: string,
  timberProjects: TimberProject[],
  taskGroups: TaskGroup[],
  projectTasks: ProjectTask[],
): ProjectTask | null {
  const family = kind === 'timber' ? 'procurement' : 'production';
  const focus = openFocusParent(projectId, family, timberProjects, taskGroups, projectTasks);
  if (!hasWorkOrder && !focus) return null;
  return focus;
}

export function buildForecastQuoteCoverageMarkers(
  input: BuildForecastQuoteCoverageMarkersInput,
): ForecastQuoteCoverageMarker[] {
  const now = input.now ?? new Date();
  const horizonKeys = horizonKeysFromInput(input);
  const deals = dealById(input.enhancedDeals);
  const dealIds = new Set<string>();
  for (const row of input.moneyInRows) {
    if (row.dealId) dealIds.add(row.dealId);
  }
  for (const p of input.timberProjects) {
    if (p.dealId) dealIds.add(p.dealId);
  }

  const markers: ForecastQuoteCoverageMarker[] = [];
  const seen = new Set<string>();

  for (const dealId of Array.from(dealIds)) {
    const deal = deals.get(dealId);
    if (!isQualifyingHubProject(dealId, deal, input.timberProjects)) continue;
    if (!dealHasForecastRevenueExposure(dealId, input.moneyInRows, horizonKeys, now)) continue;

    const projectId =
      input.projectIdByDealId.get(dealId)
      ?? pickTimberProjectForDealSchedule(
        input.timberProjects,
        dealId,
        input.projectTasks,
        input.taskGroups,
      )?.id;
    if (!projectId) continue;

    const projectLabel =
      input.projectNameById.get(projectId)
      ?? input.timberProjects.find((p) => p.id === projectId)?.name
      ?? deal?.title
      ?? dealId;

    const timberSummary = buildWorkTypeSummary(
      dealId,
      'timber_rfq',
      input.rfqProjects,
      input.rfqPackages,
      input.rfqSubmissions,
      EMPTY_ORG_LOOKUP,
    );
    const cncSummary = buildWorkTypeSummary(
      dealId,
      'cnc_quote',
      input.rfqProjects,
      input.rfqPackages,
      input.rfqSubmissions,
      EMPTY_ORG_LOOKUP,
    );

    const tracks: Array<{ kind: ForecastQuoteCoverageKind; hasWorkOrder: boolean }> = [
      { kind: 'timber', hasWorkOrder: timberSummary.hasWorkOrder },
      { kind: 'cnc', hasWorkOrder: cncSummary.hasWorkOrder },
    ];

    for (const { kind, hasWorkOrder } of tracks) {
      const coverage = trackQuoteCoverage({
        dealId,
        kind,
        proposal: input.proposalsByDeal?.[dealId],
        changeOrders: input.changeOrders,
        rfqProjects: input.rfqProjects,
        rfqPackages: input.rfqPackages,
        rfqSubmissions: input.rfqSubmissions,
        supplierBills: input.supplierBills,
      });
      if (coverage.fullyCovered) continue;
      const parent = trackShouldWarn(
        kind,
        dealId,
        hasWorkOrder,
        projectId,
        input.timberProjects,
        input.taskGroups,
        input.projectTasks,
      );
      if (!parent?.id) continue;
      const parentWindow = focusTaskWindow(parent, input.projectTasks);
      if (!parentWindow) continue;

      type QuoteCashSlice = {
        amount: number;
        task: ProjectTask;
        window: { startDate: Date; endDate: Date };
        structureId?: string;
        structureName?: string;
        uncoveredStructureNames?: string[];
      };
      const slices: QuoteCashSlice[] = [];
      if (coverage.uncovered.length > 0) {
        for (const row of coverage.uncovered) {
          const kid = findStructureSubtask(parent, input.projectTasks, row);
          const window = kid
            ? rollupDatesFromChildren([kid])
            : parentWindow;
          if (!window) continue;
          slices.push({
            amount: Math.round(row.amount),
            task: kid ?? parent,
            window,
            structureId: row.id,
            structureName: row.name,
            uncoveredStructureNames: [row.name],
          });
        }
      } else {
        slices.push({
          amount: proposalNodeQuoteAmount(
            kind,
            input.proposalsByDeal?.[dealId],
            input.changeOrders,
            dealId,
          ),
          task: parent,
          window: parentWindow,
        });
      }

      for (const slice of slices) {
        if (!slice.task.id) continue;
        const cashDate = taskWindowMidpoint(slice.window.startDate, slice.window.endDate);
        const monthKey = monthKeyFromDate(cashDate);
        if (horizonKeys.size > 0 && !horizonKeys.has(monthKey)) continue;
        const dedupe = `${dealId}:${kind}:${slice.task.id}:${slice.structureId || ''}`;
        if (seen.has(dedupe)) continue;
        seen.add(dedupe);
        markers.push({
          monthKey,
          dealId,
          projectId,
          projectLabel,
          kind,
          taskId: slice.task.id,
          taskTitle: slice.task.title || parent.title,
          taskStart: slice.window.startDate,
          cashDate,
          amount: slice.amount,
          ...(slice.structureId ? { structureId: slice.structureId } : {}),
          ...(slice.structureName ? { structureName: slice.structureName } : {}),
          ...(slice.uncoveredStructureNames?.length
            ? { uncoveredStructureNames: slice.uncoveredStructureNames }
            : {}),
        });
      }
    }
  }

  markers.sort((a, b) => {
    if (a.monthKey !== b.monthKey) return a.monthKey.localeCompare(b.monthKey);
    if (a.projectLabel !== b.projectLabel) return a.projectLabel.localeCompare(b.projectLabel);
    return a.kind.localeCompare(b.kind);
  });

  return markers;
}

export function groupForecastQuoteCoverageMarkersByMonth(
  markers: ForecastQuoteCoverageMarker[],
): Map<string, ForecastQuoteCoverageMarker[]> {
  const byMonth = new Map<string, ForecastQuoteCoverageMarker[]>();
  for (const m of markers) {
    const list = byMonth.get(m.monthKey) ?? [];
    list.push(m);
    byMonth.set(m.monthKey, list);
  }
  return byMonth;
}

export function forecastQuoteCoverageMarkerLabel(marker: ForecastQuoteCoverageMarker): string {
  const track = marker.kind === 'timber' ? 'Timber' : 'CNC';
  const when = (marker.cashDate ?? marker.taskStart).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const remaining = (marker.uncoveredStructureNames ?? []).map((name) => name.trim()).filter(Boolean);
  const forStructures = remaining.length
    ? ` for ${remaining.join(', ')}`
    : marker.structureName
      ? ` for ${marker.structureName}`
      : '';
  return `${marker.projectLabel} — ${track} quote not approved${forStructures}. Proposal pricing, not finalized (${marker.taskTitle} midpoint ${when}).`;
}

export function forecastQuoteCoverageHubHref(projectId: string): string {
  return `/project-hub/${projectId}?tab=suppliers`;
}

export function quotePlaceholderApSlicesFromMarkers(
  markers: ForecastQuoteCoverageMarker[],
): QuotePlaceholderApSlice[] {
  return markers
    .filter((m) => m.amount > 0)
    .map((m) => ({
      monthKey: m.monthKey,
      amount: m.amount,
      dealId: m.dealId,
      projectId: m.projectId,
      projectLabel: m.projectLabel,
      kind: m.kind,
      taskId: m.taskId,
      dueDate: m.cashDate ?? m.taskStart,
      ...(m.structureName ? { structureName: m.structureName } : {}),
    }));
}

/**
 * Visual top of a month's waterfall column (highest range endpoint).
 * Do not use the tallest segment's own high — AP is often taller than AR
 * and would pin the triangle to the cash line in the middle of the stack.
 */
export function quoteWarnAnchorValue(ranges: Array<unknown>, fallback: number): number {
  let peak = Number.NEGATIVE_INFINITY;
  for (const range of ranges) {
    if (!Array.isArray(range) || range.length < 2) continue;
    const a = Number(range[0]);
    const b = Number(range[1]);
    if (Number.isFinite(a)) peak = Math.max(peak, a);
    if (Number.isFinite(b)) peak = Math.max(peak, b);
  }
  return Number.isFinite(peak) ? peak : fallback;
}
